import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Breadcrumb,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import ButtonField from "../../common/htmlComponents/ButtonField";
import { FilterOutlined, UndoOutlined } from "@ant-design/icons";
import moment from "moment";
import { debounce } from "lodash";
import { getRequest, postRequest } from "../../axios";
import {
  ErrorNotificationMsg,
  ShowErrorMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import { ReactComponent as ArchiveRestore } from "../../image/ArchiveRestore.svg";

const ArchivedGeneralProcurementList = () => {
  const navigate = useNavigate();
  const formRef = useRef();

  const [clickedFilterButton, setClickedFilterButton] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [selectedRow, setSelectedRow] = useState([]);
  const [restoring, setRestoring] = useState(false);
  const [state, setState] = useState({
    page: 1,
    pagesize: 10,
    search: "",
    start_date: "",
    end_date: "",
  });

  const handleStartDateChange = (date) => {
    setStartDate(date ? moment(date).format("YYYY-MM-DD") : "");
  };

  const handleEndDateChange = (date) => {
    setEndDate(date ? moment(date).format("YYYY-MM-DD") : "");
  };

  const handleApplyFilter = () => {
    setClickedFilterButton(true);
    setState((prev) => ({
      ...prev,
      page: 1,
      start_date: startDate,
      end_date: endDate,
    }));
  };

  const handleClearFields = () => {
    setStartDate("");
    setEndDate("");
    formRef.current?.setFieldsValue({
      start_date: null,
      end_date: null,
    });
  };

  const handleReset = () => {
    setClickedFilterButton(false);
    setStartDate("");
    setEndDate("");
    setSearchInputValue("");
    setState({
      page: 1,
      pagesize: 10,
      search: "",
      start_date: "",
      end_date: "",
    });
    formRef.current?.resetFields();
  };

  const { Search } = Input;

  const applySearch = (value) => {
    setState((prev) => ({
      ...prev,
      page: 1,
      search: value || "",
    }));
  };

  const debouncedSearchRef = useRef(
    debounce((value) => applySearch(value), 600),
  );

  useEffect(
    () => () => {
      debouncedSearchRef.current?.cancel?.();
    },
    [],
  );

  const onSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearchRef.current?.(value);
  };

  const onSearchSubmit = (value) => {
    debouncedSearchRef.current?.cancel?.();
    setSearchInputValue(value || "");
    applySearch(value || "");
  };

  const fetchArchivedRfqs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: state.page,
        pagesize: state.pagesize,
      };
      if (state.search?.trim()) params.search = state.search.trim();
      if (state.start_date) params.start_date = state.start_date;
      if (state.end_date) params.end_date = state.end_date;

      const res = await getRequest("general-rfq-archive/", params);
      const payload = res?.data;

      const count =
        payload?.count ??
        payload?.results?.count ??
        payload?.results?.data?.length ??
        0;
      setTotalCount(count);

      const results = Array.isArray(payload?.results?.data)
        ? payload.results.data
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
      setTableData(results);
    } catch (err) {
      ShowErrorMsg(err);
      setTableData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    state.page,
    state.pagesize,
    state.search,
    state.start_date,
    state.end_date,
  ]);

  useEffect(() => {
    fetchArchivedRfqs();
  }, [fetchArchivedRfqs]);

  const handleRestore = useCallback(
    async (id = "") => {
      const ids = selectedRow.length > 0 ? [...selectedRow] : [];
      if (ids.length === 0 && id !== "" && id != null) {
        ids.push(id);
      }
      if (ids.length === 0) {
        message.warning("No RFQ selected to restore.");
        return;
      }

      try {
        setRestoring(true);
        const apiResponse = await postRequest("general-rfq-restore/", {
          deleted: ids,
        });
        const data = apiResponse?.data;
        const succeeded =
          data?.success === true ||
          data?.success === "true" ||
          (data?.success !== false && (apiResponse?.status ?? 200) < 400);
        if (succeeded) {
          const successText =
            (typeof data?.message === "string" && data.message) ||
            "RFQ restored successfully.";
          SuccessNotificationMsg(successText);
          setSelectedRow([]);
          fetchArchivedRfqs();
        } else {
          const failText =
            (typeof data?.message === "string" && data.message) ||
            "Failed to restore RFQ.";
          ErrorNotificationMsg(failText);
        }
      } catch (err) {
        ShowErrorMsg(err);
      } finally {
        setRestoring(false);
      }
    },
    [fetchArchivedRfqs, selectedRow],
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedRow,
      onChange: (selectedRowKeys) => setSelectedRow(selectedRowKeys),
    }),
    [selectedRow],
  );

  const renderRfqType = (v) => {
    const k = String(v ?? "").toLowerCase();
    if (k === "quick") return "Quick RFQ";
    if (k === "general") return "General RFQ";
    return v ?? "—";
  };

  const formatDate = (raw) => {
    if (!raw) return "—";
    const m = moment(raw);
    return m.isValid() ? m.format("DD-MM-YY") : String(raw);
  };

  const formatDateTime = (raw) => {
    if (!raw) return "—";
    const m = moment(raw);
    return m.isValid() ? m.format("DD-MM-YY HH:mm") : String(raw);
  };

  const formatAmount = (raw, currency) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return "—";
    const symbol = currency === "INR" ? "₹" : currency ? `${currency} ` : "";
    return `${symbol}${n.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })}`;
  };

  const renderRfqStatus = (status) => {
    if (!status) return "—";
    const s = String(status).toLowerCase();
    let color = "default";
    if (s.includes("draft")) color = "default";
    else if (s.includes("under approval") || s.includes("pending"))
      color = "processing";
    else if (s.includes("sent") || s.includes("open")) color = "processing";
    else if (s.includes("approve")) color = "success";
    else if (s.includes("reject") || s.includes("cancel")) color = "error";
    else if (s.includes("close")) color = "warning";
    return <Tag color={color}>{String(status)}</Tag>;
  };

  const itemColumns = useMemo(
    () => [
      {
        title: "Sr.",
        key: "sr",
        width: 60,
        render: (_, __, idx) => idx + 1,
      },
      {
        title: "Item Description",
        dataIndex: "item_description",
        key: "item_description",
        render: (v) => v ?? "—",
      },
      {
        title: "Approved Make",
        dataIndex: "approved_make",
        key: "approved_make",
        width: 180,
        render: (v) => v ?? "—",
      },
      {
        title: "Qty.",
        dataIndex: "quantity",
        key: "quantity",
        width: 110,
        align: "right",
        render: (v) => {
          const n = Number(v);
          return Number.isFinite(n)
            ? n.toLocaleString("en-IN", { maximumFractionDigits: 3 })
            : v ?? "—";
        },
      },
      {
        title: "UoM",
        dataIndex: "unit_of_measurement",
        key: "unit_of_measurement",
        width: 110,
        render: (v) => v ?? "—",
      },
      {
        title: "Unit Rate",
        dataIndex: "unit_rate",
        key: "unit_rate",
        width: 130,
        align: "right",
        render: (v) => {
          const n = Number(v);
          return Number.isFinite(n)
            ? n.toLocaleString("en-IN", {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })
            : v ?? "—";
        },
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: 140,
        align: "right",
        render: (v, record) => {
          const n = Number(v);
          const currency = record?.__parentCurrency;
          if (!Number.isFinite(n)) return v ?? "—";
          const symbol =
            currency === "INR" ? "₹" : currency ? `${currency} ` : "";
          return `${symbol}${n.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}`;
        },
      },
      {
        title: "Remarks from Buyer",
        dataIndex: "remarks_from_buyer",
        key: "remarks_from_buyer",
        render: (v) => v ?? "—",
      },
    ],
    [],
  );

  const renderExpandedItems = (record) => {
    const items = Array.isArray(record?.items) ? record.items : [];
    if (!items.length) {
      return (
        <div style={{ padding: "8px 12px", color: "#6B7280" }}>
          No items in this RFQ.
        </div>
      );
    }
    const dataWithCurrency = items.map((it) => ({
      ...it,
      __parentCurrency: record?.currency,
    }));
    return (
      <Table
        size="small"
        columns={itemColumns}
        dataSource={dataWithCurrency}
        pagination={false}
        rowKey={(it, idx) => it?.id ?? `${record?.id ?? "row"}-${idx}`}
        scroll={{ x: 900 }}
        style={{ margin: "8px 0" }}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        title: "RFQ #",
        dataIndex: "id",
        key: "id",
        width: 90,
        render: (v, record) =>
          v != null ? `RFQ-${v}` : record?.rfq_number ?? "—",
      },
      {
        title: "RFQ Type",
        dataIndex: "rfq_type",
        key: "rfq_type",
        width: 130,
        render: renderRfqType,
      },
      {
        title: "Company",
        dataIndex: "company_name",
        key: "company_name",
        render: (v) => v ?? "—",
      },
      {
        title: "Created By",
        dataIndex: "created_by_name",
        key: "created_by_name",
        width: 140,
        render: (v) => v ?? "—",
      },
      {
        title: "Created Date",
        dataIndex: "created_at",
        key: "created_at",
        width: 130,
        render: (v, record) =>
          formatDate(v ?? record?.rfq_created_date ?? null),
      },
      {
        title: "Bid Due Date & Time",
        dataIndex: "rfq_bid_due_date_time",
        key: "rfq_bid_due_date_time",
        width: 170,
        render: (v) => formatDateTime(v),
      },
      {
        title: "Delivery Date",
        dataIndex: "delivery_date",
        key: "delivery_date",
        width: 130,
        render: (v) => formatDate(v),
      },
      {
        title: "Items",
        dataIndex: "items",
        key: "items",
        width: 90,
        align: "center",
        render: (v) => (Array.isArray(v) ? v.length : 0),
      },
      {
        title: "Total Amount",
        dataIndex: "total_amount",
        key: "total_amount",
        width: 150,
        align: "right",
        render: (v, record) => formatAmount(v, record?.currency),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 180,
        render: (v, record) => renderRfqStatus(v ?? record?.rfq_status ?? null),
      },
      {
        title: "Action",
        key: "operation",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Space size="small">
            <Popconfirm
              className="action"
              title="Restore this RFQ to the active list?"
              okText="Yes"
              cancelText="No"
              placement="left"
              onConfirm={() => handleRestore(record.id)}
            >
              <Tooltip title="Restore" placement="left" color="#094E8F">
                <ArchiveRestore className="ArchiverestorImg" />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleRestore],
  );

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>Archived — Quick / General RFQ</h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">Dashboard</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/general-procurement-list">
                  Quick / General RFQ — History
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Archive</Breadcrumb.Item>
            </Breadcrumb>
          </div>

          <Space>
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              htmlType="button"
              label="Back to list"
              onClick={() => navigate("/general-procurement-list")}
            />
          </Space>
        </div>
      </div>

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <div className="filterrow">
            <div className="filtercol">
              <Form autoComplete="off" ref={formRef}>
                <Space wrap>
                  <Col xs={24} className="TextFeild gutter-row">
                    <Form.Item name="start_date">
                      <DatePicker
                        format="DD-MM-YY"
                        placeholder="RFQ created from"
                        onChange={handleStartDateChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} className="TextFeild gutter-row">
                    <Form.Item name="end_date">
                      <DatePicker
                        format="DD-MM-YY"
                        placeholder="RFQ created to"
                        onChange={handleEndDateChange}
                      />
                    </Form.Item>
                  </Col>

                  <Button
                    type="primary"
                    htmlType="button"
                    icon={
                      <Tooltip title="Apply" color="#094E8F">
                        <FilterOutlined style={{ fontSize: "20px" }} />
                      </Tooltip>
                    }
                    style={{
                      backgroundColor: "#094e8f",
                      color: "#ffffff",
                      border: "none",
                      width: "40px",
                      height: "40px",
                      boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                      borderRadius: "4px",
                    }}
                    onClick={startDate || endDate ? handleApplyFilter : null}
                  />
                  <Button
                    type="primary"
                    htmlType="button"
                    icon={
                      <Tooltip title="Reset" color="#094E8F">
                        <UndoOutlined style={{ fontSize: "20px" }} />
                      </Tooltip>
                    }
                    style={{
                      backgroundColor: "#094e8f",
                      color: "#ffffff",
                      border: "none",
                      width: "40px",
                      height: "40px",
                      boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                      borderRadius: "4px",
                    }}
                    onClick={
                      clickedFilterButton ? handleReset : handleClearFields
                    }
                  />
                </Space>
              </Form>
            </div>

            <div className="btnscol">
              <Space>
                <div className="searchwrp">
                  <Space direction="vertical">
                    <Search
                      placeholder="Search"
                      onSearch={onSearchSubmit}
                      allowClear
                      style={{ width: 250 }}
                      value={searchInputValue}
                      onChange={onSearchInputChange}
                    />
                  </Space>
                </div>
              </Space>
              {selectedRow.length > 0 && (
                <Popconfirm
                  className="action"
                  title="Restore the selected RFQs to the active list?"
                  okText="Yes"
                  cancelText="No"
                  placement="left"
                  onConfirm={() => handleRestore()}
                >
                  <ButtonField
                    className="BlueShadowBtn"
                    type="primary"
                    htmlType="button"
                    label={`Restore (${selectedRow.length})`}
                    loading={restoring}
                  />
                </Popconfirm>
              )}
            </div>
          </div>

          <div className="tablewrp">
            <Table
              scroll={{ x: 1500 }}
              columns={columns}
              dataSource={tableData}
              loading={loading}
              rowSelection={rowSelection}
              rowKey={(record) =>
                record?.id ??
                record?.rfq_number ??
                `${record?.created_at ?? ""}-${record?.created_by ?? ""}`
              }
              pagination={{
                current: state.page,
                pageSize: state.pagesize,
                total: totalCount,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                locale: { items_per_page: "Items / Page" },
                showTotal: (total) => `Total ${total} items`,
              }}
              onChange={(pagination) => {
                setState((prev) => ({
                  ...prev,
                  page: pagination.current,
                  pagesize: pagination.pageSize,
                }));
              }}
              expandable={{
                expandedRowRender: renderExpandedItems,
                rowExpandable: (record) =>
                  Array.isArray(record?.items) && record.items.length > 0,
              }}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default ArchivedGeneralProcurementList;
